import { HEARING_AID_TYPES, WARRANTY_SUBTYPE } from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import { InventoryProduct } from "services/types/inventory-products.type";
import { Product, ProductCatalogOption } from "services/types/products.type";
import { formatMoney } from "utils/common";
import { hasReceiverLength } from "utils/inventory-products.utils";
import {
  formatInventoryProductToProductCatalogOption,
  isSerializedProduct
} from "utils/products.utils";

import { ProductSelectedInfo } from "./use-product-catalog-field.hook";

const formatInventoryProductOption = (
  inventoryProduct: InventoryProduct,
  name: string,
  getIsProductSelectedInfo: (
    product: ProductCatalogOption,
    name: string
  ) => ProductSelectedInfo
): InventoryProduct | null => {
  const { selectedQuantity, isSelectedByInventoryId } =
    getIsProductSelectedInfo(
      formatInventoryProductToProductCatalogOption(inventoryProduct)!,
      name
    );
  const available_quantity =
    Number(inventoryProduct.available_quantity || 0) - selectedQuantity;
  if (
    isSelectedByInventoryId &&
    (isSerializedProduct(
      inventoryProduct.product.master_product.type,
      inventoryProduct.product.master_product.subtype,
      false
    ) ||
      available_quantity <= 0)
  ) {
    return null;
  } else {
    return {
      ...inventoryProduct,
      available_quantity
    };
  }
};

export const getSelectedInventoryProductId = (
  inventoryProduct: InventoryProduct,
  name: string,
  getIsProductSelectedInfo: (
    product: ProductCatalogOption,
    name: string
  ) => ProductSelectedInfo
): InventoryProduct | null => {
  const { selectedQuantity, isSelectedByInventoryId } =
    getIsProductSelectedInfo(
      formatInventoryProductToProductCatalogOption(inventoryProduct)!,
      name
    );
  const available_quantity =
    Number(inventoryProduct.available_quantity || 0) - selectedQuantity;
  if (
    isSelectedByInventoryId &&
    (isSerializedProduct(
      inventoryProduct.product.master_product.type,
      inventoryProduct.product.master_product.subtype,
      false
    ) ||
      available_quantity <= 0)
  ) {
    return null;
  } else {
    return {
      ...inventoryProduct,
      available_quantity
    };
  }
};

export const formatOptionsWithSelectedProducts = (
  inventoryProducts: InventoryProduct[],
  name: string,
  getIsProductSelectedInfo: (
    product: ProductCatalogOption,
    name: string
  ) => ProductSelectedInfo
): InventoryProduct[] => {
  return inventoryProducts
    .map((inventoryProduct) =>
      formatInventoryProductOption(
        inventoryProduct,
        name,
        getIsProductSelectedInfo
      )
    )
    .filter(Boolean) as InventoryProduct[];
};

export const getProductText = (
  product: Product,
  showComponentPrice: boolean = false
) => {
  const texts = [product.display_name ?? product.name].filter(
    (text) => text?.trim().length
  );
  if (showComponentPrice && Number(product.component_price) > 0) {
    texts.push(`(+${formatMoney(Number(product.component_price), 2, 2)})`);
  }
  return texts.join(" ");
};

export const getSpecificationPreviewText = (option: ProductCatalogOption) => {
  const { type, subtype } = option.master_product || {};

  const result: string[] = [];

  if (option.inventory_specification) {
    const spec = option.inventory_specification as any;
    if (spec.ear) result.push(`Ear: ${spec.ear}`);
    if (spec.color) result.push(`Color: ${spec.color}`);
    if (spec.material) result.push(`Material: ${spec.material}`);
    if (hasReceiverLength(spec.length)) result.push(`Length: ${spec.length}`);
    if (spec.gain) result.push(`Gain: ${spec.gain}`);
    if (spec.size) result.push(`Size: ${spec.size}`);
    if (spec.type) result.push(`Type: ${spec.type}`);
  } else if (option.master_product?.specification) {
    const spec = option.master_product?.specification as any;
    if (spec.type) {
      if (type === HEARING_AID_PRODUCT_TYPE) {
        result.push(`Type: ${HEARING_AID_TYPES.getFromCode(spec.type)}`);
      } else if (type === ACCESSORY_PRODUCT_TYPE) {
        result.push(`Type: ${spec.type}`);
      }
    }
    if (spec.size) result.push(`Size: ${spec.size}`);
    if (type === SERVICE_PRODUCT_TYPE && subtype === WARRANTY_SUBTYPE) {
      result.push(`Months: ${spec.warranty_months || 0}`);
    }
  } else return [];

  return result;
};
